/**
 * Parses an account number from a scanned barcode string.
 *
 * If a regex pattern with named capture groups is provided (via kiosk config),
 * it is used to extract those groups, which are then joined with "-" to produce
 * the account number.
 *
 * If no pattern is provided (or it is null/undefined), the barcode is returned
 * unchanged.
 *
 * Returns the original barcode string unchanged if the pattern does not match.
 *
 * @param barcode   The raw scanned barcode string (should already be trimmed).
 * @param pattern   Optional regex pattern string with named capture groups from config.
 * @returns         The parsed account number, or the original barcode if parsing fails.
 *
 * @example
 * // With a config pattern (named groups are joined with "-"):
 * parseBarcode("XX123456YY78ZZZZZZZZ", "^.{2}(?<locationCode>\\d{6}).{2}(?<occupantCode>\\d{2})")
 * // => "123456-78"
 *
 * // Without a config pattern (pass-through):
 * parseBarcode("XX123456YY78ZZZZZZZZ")
 * // => "XX123456YY78ZZZZZZZZ"
 */
export function parseBarcode(barcode: string, pattern?: string | null): string {
	if (pattern) {
		try {
			const regex = new RegExp(pattern);
			const match = regex.exec(barcode);

			if (match?.groups && Object.keys(match.groups).length > 0) {
				return Object.values(match.groups)
					.filter((v) => v !== undefined)
					.join('-');
			}

			if (match) {
				return match[0];
			}
		} catch {
			// Invalid regex in config — fall through to legacy logic
		}
	}

	return barcode;
}
